import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { Copy, Check } from 'lucide-react';

export default function CopyableUrl({ url, maxWidth = '100%', sx = {} }) {
  const [copied, setCopied] = useState(false);

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.warn('Fallback al copiar falló:', err);
    }
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!url) return;

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          fallbackCopy(url);
        });
    } else {
      fallbackCopy(url);
    }
  };

  return (
    <Box
      className="selectable-url"
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1.5,
        py: 0.6,
        borderRadius: '10px',
        background: 'rgba(0, 212, 255, 0.08)',
        border: '1px solid rgba(0, 212, 255, 0.25)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        maxWidth: maxWidth,
        width: '100%',
        boxSizing: 'border-box',
        cursor: 'text',
        userSelect: 'text',
        WebkitUserSelect: 'text',
        MozUserSelect: 'text',
        msUserSelect: 'text',
        ...sx,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: '0.78rem',
          fontFamily: 'monospace',
          color: '#00ffd5',
          fontWeight: 600,
          letterSpacing: '0.2px',
          wordBreak: 'break-all',
          userSelect: 'text',
          WebkitUserSelect: 'text',
          MozUserSelect: 'text',
          cursor: 'text',
          flex: 1,
          textAlign: 'left',
        }}
      >
        {url}
      </Typography>

      <Tooltip title={copied ? '¡Copiado!' : 'Copiar enlace'} arrow>
        <IconButton
          size="small"
          onClick={handleCopy}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            p: 0.6,
            ml: 0.5,
            color: copied ? '#00ff88' : 'rgba(255, 255, 255, 0.65)',
            background: copied ? 'rgba(0, 255, 136, 0.15)' : 'rgba(255, 255, 255, 0.05)',
            border: copied ? '1px solid rgba(0, 255, 136, 0.4)' : '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            flexShrink: 0,
            '&:hover': {
              color: copied ? '#00ff88' : '#00ffd5',
              background: 'rgba(0, 212, 255, 0.15)',
              borderColor: 'rgba(0, 212, 255, 0.4)',
            },
          }}
        >
          {copied ? <Check size={14} color="#00ff88" /> : <Copy size={14} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}
